import { useEffect, useState } from "react";
import { CheckCircle2, ArrowRight } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { useLang } from "@/context/LanguageContext";
import { api } from "@/lib/api";

const init = { name: "", business_name: "", email: "", phone: "", business_type: "", location: "", website_status: "", needs: [], preferred_language: "", message: "" };

export default function ContactPage() {
  const { t, lang } = useLang();
  const [form, setForm] = useState(init);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  useEffect(() => {
    document.title = "Contact — Bezalel Visionary · Book a Call";
    window.scrollTo(0, 0);
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleNeed = (n) =>
    setForm((f) => ({ ...f, needs: f.needs.includes(n) ? f.needs.filter((x) => x !== n) : [...f.needs, n] }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) {
      setError(t.contact.required);
      return;
    }
    setStatus("sending");
    try {
      await api.post("/enquiries", { ...form, preferred_language: form.preferred_language || lang });
      setStatus("success");
    } catch (err) {
      setStatus("idle");
      setError(t.contact.error);
    }
  };

  return (
    <main data-testid="contact-page">
      <Navbar />
      <div className="bg-[#F7F3EC] pt-32 lg:pt-40 pb-20 lg:pb-32 min-h-screen">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="eyebrow-label mb-6">{t.contact.eyebrow}</p>
            <h1 className="font-serif text-[#191713] text-4xl sm:text-5xl leading-tight">{t.contact.title}</h1>
            <p className="mt-5 text-[#191713]/70 max-w-md leading-relaxed">{t.contact.sub}</p>
            <div className="mt-10 space-y-3 text-sm text-[#191713]/60">
              <p className="flex items-center gap-3"><span className="w-6 h-px bg-[#B8933F]" /> Dubai · Sharjah · Deira · UAE</p>
              <p className="flex items-center gap-3"><span className="w-6 h-px bg-[#B8933F]" /> FR · EN · PT</p>
            </div>
          </div>

          <div className="lg:col-span-7">
            {status === "success" ? (
              <div className="bg-white border border-[#B8933F]/50 p-10 lg:p-14 text-center" data-testid="contact-success">
                <CheckCircle2 size={40} className="text-[#B8933F] mx-auto" strokeWidth={1.5} />
                <h2 className="mt-6 font-serif text-2xl text-[#191713]">{t.contact.successTitle}</h2>
                <p className="mt-3 text-[#191713]/70">{t.contact.successBody}</p>
                <button
                  data-testid="contact-send-another"
                  onClick={() => { setForm(init); setStatus("idle"); }}
                  className="btn-outline-dark mt-8"
                >
                  {t.contact.another}
                </button>
              </div>
            ) : (
              <form onSubmit={submit} noValidate className="bg-white border border-[#191713]/10 p-8 lg:p-12 grid grid-cols-1 sm:grid-cols-2 gap-6" data-testid="contact-form">
                <div>
                  <label className="field-label" htmlFor="c-name">{t.contact.name} *</label>
                  <input id="c-name" data-testid="contact-input-name" className="field-input" value={form.name} onChange={set("name")} required />
                </div>
                <div>
                  <label className="field-label" htmlFor="c-biz">{t.contact.businessName}</label>
                  <input id="c-biz" data-testid="contact-input-business" className="field-input" value={form.business_name} onChange={set("business_name")} />
                </div>
                <div>
                  <label className="field-label" htmlFor="c-email">{t.contact.email} *</label>
                  <input id="c-email" type="email" data-testid="contact-input-email" className="field-input" value={form.email} onChange={set("email")} required />
                </div>
                <div>
                  <label className="field-label" htmlFor="c-phone">{t.contact.phone}</label>
                  <input id="c-phone" data-testid="contact-input-phone" className="field-input" value={form.phone} onChange={set("phone")} />
                </div>
                <div>
                  <label className="field-label" htmlFor="c-type">{t.contact.businessType}</label>
                  <select id="c-type" data-testid="contact-select-type" className="field-input" value={form.business_type} onChange={set("business_type")}>
                    <option value="">{t.contact.select}</option>
                    {t.contact.businessTypes.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
                <div>
                  <label className="field-label" htmlFor="c-loc">{t.contact.location}</label>
                  <select id="c-loc" data-testid="contact-select-location" className="field-input" value={form.location} onChange={set("location")}>
                    <option value="">{t.contact.select}</option>
                    {t.contact.locations.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
                <div>
                  <label className="field-label" htmlFor="c-web">{t.contact.websiteStatus}</label>
                  <select id="c-web" data-testid="contact-select-website" className="field-input" value={form.website_status} onChange={set("website_status")}>
                    <option value="">{t.contact.select}</option>
                    {t.contact.websiteOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
                <div>
                  <label className="field-label" htmlFor="c-lang">{t.contact.preferredLanguage}</label>
                  <select id="c-lang" data-testid="contact-select-language" className="field-input" value={form.preferred_language} onChange={set("preferred_language")}>
                    <option value="">{lang}</option>
                    <option value="FR">Français</option>
                    <option value="EN">English</option>
                    <option value="PT">Português</option>
                  </select>
                </div>
                <fieldset className="sm:col-span-2">
                  <legend className="field-label">{t.contact.needs}</legend>
                  <div className="flex flex-wrap gap-2">
                    {t.contact.needsOptions.map((n) => (
                      <button
                        key={n} type="button"
                        data-testid={`contact-need-${n.replace(/[^a-z]/gi, "-").toLowerCase()}`}
                        onClick={() => toggleNeed(n)}
                        aria-pressed={form.needs.includes(n)}
                        className={`px-4 py-2 text-sm border transition-colors duration-200 ${
                          form.needs.includes(n)
                            ? "bg-[#191713] text-[#F7F3EC] border-[#191713]"
                            : "border-[#191713]/20 text-[#191713]/70 hover:border-[#B8933F]"
                        }`}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                </fieldset>
                <div className="sm:col-span-2">
                  <label className="field-label" htmlFor="c-msg">{t.contact.message}</label>
                  <textarea id="c-msg" rows={4} data-testid="contact-input-message" className="field-input resize-none" placeholder={t.contact.messagePh} value={form.message} onChange={set("message")} />
                </div>
                {error && <p className="sm:col-span-2 text-sm text-red-700" role="alert" data-testid="contact-error">{error}</p>}
                <div className="sm:col-span-2">
                  <button type="submit" data-testid="contact-submit-button" disabled={status === "sending"} className="btn-gold w-full sm:w-auto disabled:opacity-60">
                    {status === "sending" ? t.contact.sending : t.contact.submit} <ArrowRight size={16} />
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </main>
  );
}
